/**
 * Filename-to-document-type classification for the document preview surfaces.
 *
 * Purely lexical: it reads an extension off a string and returns a union
 * member. Split out of `ChatDocumentPreview.tsx` (now under
 * `src/components/library/document-preview/`) so callers can ask "is this
 * previewable?" without importing the component, which pulls in the whole
 * lazy preview stack. The component re-exports these names unchanged.
 */

/**
 * Supported document types for preview
 */
export type PreviewableDocumentType =
  | 'pdf'
  | 'txt'
  | 'xml'
  | 'md'
  | 'xlsx'
  | 'xls'
  | 'csv'
  | 'ods'
  | 'docx'
  | 'image';

/**
 * Document types that are NOT supported for preview (yet)
 */
export type UnsupportedDocumentType = 'doc' | 'unknown';

export type DocumentType = PreviewableDocumentType | UnsupportedDocumentType;

/**
 * Get the document type from a filename
 */
export function getDocumentType(filename: string): DocumentType {
  const extension = filename.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'txt':
      return 'txt';
    case 'xml':
      return 'xml';
    case 'md':
    case 'markdown':
      return 'md';
    case 'xlsx':
      return 'xlsx';
    case 'xls':
      return 'xls';
    case 'csv':
      return 'csv';
    case 'ods':
      return 'ods';
    case 'doc':
      return 'doc';
    case 'docx':
      return 'docx';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'svg':
    case 'bmp':
      return 'image';
    default:
      return 'unknown';
  }
}

/**
 * Check if a document type is previewable
 */
export function isPreviewableDocument(
  type: DocumentType
): type is PreviewableDocumentType {
  return (
    type === 'pdf' ||
    type === 'txt' ||
    type === 'xml' ||
    type === 'md' ||
    type === 'xlsx' ||
    type === 'xls' ||
    type === 'csv' ||
    type === 'ods' ||
    type === 'docx' ||
    type === 'image'
  );
}

/**
 * Check if a filename is previewable
 */
export function isPreviewableFilename(filename: string): boolean {
  return isPreviewableDocument(getDocumentType(filename));
}
